import { baseApi } from '../../../shared/api/baseApi';
import type { ApiPath, Paginated } from '../../../shared/api/types';
import { notifyOnError } from '../../../shared/api/onQueryError';
import type { Project } from '../../../types/projectsTable';
import type { Position, PositionDraft } from '../../../types/project';
import type { ResultPosition } from '../../../types/results';
import { isProductFinished } from '../model/dto';
import type { ProductAnalogs, ProductMarketplaces, ProductSupplierPortal } from '../../../types/results';
import type { ReportDelivery } from '../model/types';
import type {
  CreateProjectResponse,
  ProductDto,
  ProductMarketplacesDto,
  ProductSupplierPortalDto,
  ProjectDto,
  ReportDto,
  FixDimensionsBody,
  RestartProductBody,
  StartProductsSearchResponse,
  UploadPositionsResponse,
  ProductAnalogsDto,
  FixAnalogDimensionsBody,
} from '../model/dto';
import type { StartProductsSearchArgs } from '../model/types';
import {
  mapProduct,
  mapProject,
  mapResultPosition,
  positionToBody,
  mapProductMarketplaces,
  mapSupplierPortal,
  mapReportDelivery,
  mapProductAnalogs
} from '../model/mappers';
import { DEFAULT_PAGE_SIZE } from '../../../shared/config/pagination';

export const PROJECTS_PAGE_SIZE = 500;

/* ─── заметка проекта: 1 проект = 1 заметка ─── */

export const PROJECT_NOTE_MAX_LENGTH = 10_000;

export interface ProjectNote {
  id: string;
  project_id: string;
  text: string;
  author_email: string;
  created_at: string;
  updated_at: string;
}

export interface SaveProjectNoteArgs {
  projectId: string;
  text: string;
  /** при уходе со страницы — чтобы запрос не оборвался */
  keepalive?: boolean;
}

const productsList = (reportId: string) => ({ type: 'Products' as const, id: reportId });

export interface ProductsPage {
  pages: number;
  items: Position[];
  results: ResultPosition[];
  total: number;
  finished: number;
  isSearching: boolean;
  isDeliveryPending: boolean;
  deliveryAvgTotal: number;
}

export interface CreateProjectPayload {
  name: string;
  organizationId: string;
  address?: string;
  file?: File | null;
  useGpt?: boolean;
}

export interface AddProductSourceArgs {
  productId: string;
  reportId: string;
  url: string;
  price: number;
  title?: string;
}

export const projectApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getProjects: builder.query<Project[], { organizationId: string }>({
      query: ({ organizationId }) => ({
        url: `/api/organizations/${organizationId}/projects` satisfies ApiPath,
        params: { page_size: PROJECTS_PAGE_SIZE, page_num: 1 },
      }),
      transformResponse: (response: Paginated<ProjectDto>) =>
        (response.data ?? []).map(mapProject),
      providesTags: ['Projects'],
      onQueryStarted: (_arg, { queryFulfilled }) => {
        void notifyOnError(queryFulfilled, 'Не удалось загрузить проекты');
      },
    }),

    createProject: builder.mutation<Project, CreateProjectPayload>({
      query: ({ name, organizationId, address = '', file, useGpt = false }) => {
        const body = new FormData();
        body.append('name', name);
        body.append('organization_id', organizationId);
        body.append('address', address);

        // usegpt имеет смысл только вместе с файлом — так было в старом фронте
        if (file) {
          body.append('file', file);
          body.append('usegpt', String(useGpt));
        }

        return { url: '/api/projects' satisfies ApiPath, method: 'POST', body };
      },
      transformResponse: (response: CreateProjectResponse) => mapProject(response.project),
      invalidatesTags: ['Projects'],
      // notifyOnError здесь убран: 409 и лимит символов показываем в самой форме
    }),

    updateProject: builder.mutation<Project, { id: string; body: { name?: string } }>({
      query: ({ id, body }) => ({
        url: `/api/projects/${id}` satisfies ApiPath,
        method: 'PATCH',
        body,
      }),
      transformResponse: (response: ProjectDto) => mapProject(response),
      invalidatesTags: ['Projects'],
      onQueryStarted: (_arg, { queryFulfilled }) => {
        void notifyOnError(queryFulfilled, 'Не удалось сохранить проект');
      },
    }),

    deleteProject: builder.mutation<void, string>({
      query: (id) => ({ url: `/api/projects/${id}` satisfies ApiPath, method: 'DELETE' }),
      invalidatesTags: ['Projects'],
      onQueryStarted: (_arg, { queryFulfilled }) => {
        void notifyOnError(queryFulfilled, 'Не удалось удалить проект');
      },
    }),

    /* ─── заметка проекта ─── */

    /** null — заметки ещё нет */
    getProjectNote: builder.query<ProjectNote | null, string>({
      query: (projectId) => ({ url: `/api/projects/${projectId}/note` }),
      transformResponse: (response: ProjectNote | null) => response ?? null,
      // ошибки показываем в самом окне заметки
    }),

    /** Текст заменяется целиком; пустой текст удаляет заметку (ответ — null). */
    saveProjectNote: builder.mutation<ProjectNote | null, SaveProjectNoteArgs>({
      query: ({ projectId, text, keepalive }) => ({
        url: `/api/projects/${projectId}/note`,
        method: 'PUT',
        body: { text },
        ...(keepalive ? { keepalive: true } : {}),
      }),
      transformResponse: (response: ProjectNote | null) => response ?? null,
      // Кладём ответ прямо в кэш GET — без лишнего перезапроса и без нового tagType.
      onQueryStarted: async ({ projectId }, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          dispatch(projectApi.util.upsertQueryData('getProjectNote', projectId, data));
        } catch {
          /* ошибка обрабатывается в UI */
        }
      },
    }),

    deleteProjectNote: builder.mutation<void, string>({
      query: (projectId) => ({ url: `/api/projects/${projectId}/note`, method: 'DELETE' }),
      onQueryStarted: async (projectId, { dispatch, queryFulfilled }) => {
        void notifyOnError(queryFulfilled, 'Не удалось удалить заметку');
        try {
          await queryFulfilled;
          dispatch(projectApi.util.upsertQueryData('getProjectNote', projectId, null));
        } catch {
          /* уведомление уже показано */
        }
      },
    }),

    /* ─── отчёт: обязательное звено между проектом и позициями ─── */

    getProjectReport: builder.query<ReportDto | null, string>({
      query: (projectId) => ({
        url: `/api/projects/${projectId}/reports` satisfies ApiPath,
      }),
      transformResponse: (response: { reports?: ReportDto[] }) => response.reports?.[0] ?? null,
      providesTags: ['Report'],
      onQueryStarted: (_arg, { queryFulfilled }) => {
        void notifyOnError(queryFulfilled, 'Не удалось загрузить отчёт проекта');
      },
    }),

    /* ─── позиции заявки ─── */

    getProducts: builder.query<ProductsPage, { reportId: string; page?: number }>({
      query: ({ reportId, page = 1 }) => ({
        url: `/api/reports/${reportId}/products` satisfies ApiPath,
        params: {
          page_size: DEFAULT_PAGE_SIZE,
          page_num: page,
          sort_by: 'created_at',
          sort_order: 'asc',
        },
      }),
      transformResponse: (
        response: Paginated<ProductDto> & { total_avg_delivery?: number },
      ): ProductsPage => {
        const rows = response.data ?? [];
        const finished = rows.filter(isProductFinished).length;

        const total = response.count ?? rows.length;

        return {
          items: rows.map(mapProduct),
          results: rows.map(mapResultPosition),
          total,
          pages: response.pages ?? 1,
          finished,
          isSearching: rows.length > 0 && finished < rows.length,
          isDeliveryPending: rows.some(
            (row) => row.delivery_status === 'processing' || row.analogs_delivery === 'processing',
          ),
          deliveryAvgTotal: response.total_avg_delivery ?? 0,
        };
      },
      providesTags: (_r, _e, { reportId }) => [productsList(reportId)],
      onQueryStarted: (_arg, { queryFulfilled }) => {
        void notifyOnError(queryFulfilled, 'Не удалось загрузить позиции заявки');
      },
    }),

    getProductMarketplaces: builder.query<ProductMarketplaces, string>({
      query: (productId) => ({
        url: `/api/products/${productId}/marketplaces` satisfies ApiPath,
      }),
      transformResponse: (response: ProductMarketplacesDto) => mapProductMarketplaces(response),
      providesTags: (_r, _e, productId) => [{ type: 'Products', id: `marketplaces:${productId}` }],
    }),

    makeScreenshots: builder.mutation<
      { report_status: string; report_screenshot_status: string },
      string
    >({
      query: (projectId) => ({
        url: '/api/screenshots/make_screenshots' satisfies ApiPath,
        method: 'POST',
        body: { project_id: projectId },
      }),
      invalidatesTags: ['Report', 'Projects'],
      onQueryStarted: (_arg, { queryFulfilled }) => {
        void notifyOnError(queryFulfilled, 'Не удалось запустить создание скриншотов');
      },
    }),

    checkScreenshotsStatus: builder.query<
      { found?: number; total?: number; report_status?: string },
      string
    >({
      query: (reportId) => ({
        url: '/api/screenshots/check_screenshot_status' satisfies ApiPath,
        method: 'POST',
        body: { report_id: reportId },
      }),
    }),

    getReportDelivery: builder.query<ReportDelivery, { projectId: string; reportId: string }>({
      query: ({ projectId }) => ({
        url: `/api/projects/${projectId}/reports` satisfies ApiPath,
      }),
      transformResponse: (response: { reports?: ReportDto[] }, _meta, { reportId }) =>
        mapReportDelivery(
          response.reports?.find((item) => item.id === reportId) ??
          response.reports?.[0] ??
          ({} as ReportDto),
        ),
      providesTags: (_r, _e, { projectId }) => [{ type: 'Report', id: projectId }],
    }),

    getSupplierPortal: builder.query<ProductSupplierPortal, string>({
      query: (productId) => ({
        url: `/api/products/${productId}/supplier-portal` satisfies ApiPath,
      }),
      transformResponse: (response: ProductSupplierPortalDto) => mapSupplierPortal(response),
      providesTags: (_r, _e, productId) => [{ type: 'Products', id: `portal:${productId}` }],
    }),

    getProductAnalogs: builder.query<ProductAnalogs, string>({
      query: (productId) => ({
        url: `/api/products/${productId}/analogs` satisfies ApiPath,
      }),
      transformResponse: (response: ProductAnalogsDto) => mapProductAnalogs(response),
      providesTags: (_r, _e, productId) => [{ type: 'Products', id: `analogs:${productId}` }],
    }),

    addProduct: builder.mutation<void, { reportId: string; draft: PositionDraft }>({
      query: ({ reportId, draft }) => ({
        url: `/api/reports/${reportId}/products` satisfies ApiPath,
        method: 'POST',
        body: positionToBody(draft),
      }),
      invalidatesTags: (_r, _e, { reportId }) => [productsList(reportId), 'Report'],
      onQueryStarted: (_arg, { queryFulfilled }) => {
        void notifyOnError(queryFulfilled, 'Не удалось добавить позицию');
      },
    }),

    updateProduct: builder.mutation<
      void,
      { reportId: string; productId: string; body: Record<string, unknown> }
    >({
      query: ({ reportId, productId, body }) => ({
        url: `/api/reports/${reportId}/products/${productId}` satisfies ApiPath,
        method: 'PATCH',
        body,
      }),
      /* Меняется количество или единица — раскрытые панели аналогов
         и маркетплейсов перезапрашивать незачем. */
      invalidatesTags: (_r, _e, { reportId }) => [productsList(reportId), 'Report'],
      onQueryStarted: (_arg, { queryFulfilled }) => {
        void notifyOnError(queryFulfilled, 'Не удалось изменить позицию');
      },
    }),

    deleteProduct: builder.mutation<void, { reportId: string; productId: string }>({
      query: ({ reportId, productId }) => ({
        url: `/api/reports/${reportId}/products/${productId}` satisfies ApiPath,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, { reportId }) => [productsList(reportId), 'Report'],
      onQueryStarted: (_arg, { queryFulfilled }) => {
        void notifyOnError(queryFulfilled, 'Не удалось удалить позицию');
      },
    }),

    uploadPositionsFile: builder.mutation<
      UploadPositionsResponse,
      { projectId: string; reportId: string; file: File }
    >({
      query: ({ projectId, reportId, file }) => {
        const body = new FormData();
        body.append('file', file);
        return {
          url: `/api/projects/${projectId}/reports/${reportId}` satisfies ApiPath,
          method: 'PUT',
          body,
        };
      },
      invalidatesTags: (_r, _e, { reportId }) => [productsList(reportId), 'Report'],
      // notifyOnError убран: ошибки формата и лимита символов показываем в UI
    }),

    setDeliveryAddress: builder.mutation<
      void,
      { reportId: string; address: string; postalCode?: string }
    >({
      query: ({ reportId, address, postalCode }) => ({
        url: `/api/reports/${reportId}/search_address` satisfies ApiPath,
        method: 'POST',
        body: { address, postal_code: postalCode },
      }),
      invalidatesTags: ['Report'],
      onQueryStarted: (_arg, { queryFulfilled }) => {
        void notifyOnError(queryFulfilled, 'Не удалось сохранить адрес доставки');
      },
    }),

    addProductSource: builder.mutation<void, AddProductSourceArgs>({
      query: ({ productId, reportId, url, price, title }) => ({
        url: `/api/products/${productId}/sources`,
        method: 'POST',
        body: {
          report_id: reportId,
          source_url: url,
          source_price: price,
          ...(title ? { source_title: title } : {}),
        },
      }),
      invalidatesTags: (_r, error, { reportId }) =>
        error ? [] : [productsList(reportId), 'Report', 'Projects'],
    }),

    deleteSources: builder.mutation<void, { reportId: string; productId: string; urls: string[] }>({
      query: ({ productId, urls }) => ({
        url: `/api/products/${productId}/sources`,
        method: 'DELETE',
        body: { urls },
      }),
      invalidatesTags: (_r, error, { reportId }) =>
        error ? [] : [productsList(reportId), 'Report', 'Projects'],
    }),

    deleteMarketplace: builder.mutation<void, { productId: string; marketplace: 'wildberries' }>({
      query: ({ productId, marketplace }) => ({
        url: `/api/products/${productId}/marketplaces/${marketplace}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, error, { productId }) =>
        error ? [] : [{ type: 'Products' as const, id: `marketplaces:${productId}` }],
    }),

    deleteSupplierPortal: builder.mutation<void, string>({
      query: (productId) => ({
        url: `/api/products/${productId}/supplier-portal`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, error, productId) =>
        error ? [] : [{ type: 'Products' as const, id: `portal:${productId}` }],
    }),

    /** Запуск расчёта габаритов и доставки по всем аналогам позиции. */
    startAnalogsLogistics: builder.mutation<
      { productId: string; status: 'pending' | 'processing' | 'done'; toCalculate: number },
      string
    >({
      query: (productId) => ({
        url: `/api/products/${productId}/analogs/logistics` satisfies ApiPath,
        method: 'POST',
      }),
      transformResponse: (response: {
        product_id: string;
        logistics_status: 'pending' | 'processing' | 'done';
        analogs_to_calculate: number;
      }) => ({
        productId: response.product_id,
        status: response.logistics_status,
        toCalculate: response.analogs_to_calculate,
      }),
      invalidatesTags: (_r, _e, productId) => [
        { type: 'Products' as const, id: `analogs:${productId}` },
      ],
      /* notifyOnError не вешаем: 409 «уже считаем» — не ошибка, обрабатывается в компоненте. */
    }),

    /* ─── поиск ─── */
    startProductsSearch: builder.mutation<StartProductsSearchResponse, StartProductsSearchArgs>({
      query: ({ projectId, reportId, products }) => ({
        url: `/api/projects/${projectId}/reports/${reportId}/search` satisfies ApiPath,
        method: 'POST',
        body: { products },
      }),
      invalidatesTags: (_r, _e, { reportId }) => [productsList(reportId), 'Report', 'Projects'],
      onQueryStarted: (_arg, { queryFulfilled }) => {
        void notifyOnError(queryFulfilled, 'Не удалось запустить поиск по позициям');
      },
    }),

    startSearch: builder.mutation<void, string>({
      query: (projectId) => ({
        url: `/api/communications/refind/${projectId}` satisfies ApiPath,
        method: 'GET',
      }),
      invalidatesTags: ['Products', 'Report', 'Projects'],
      onQueryStarted: (_arg, { queryFulfilled }) => {
        void notifyOnError(queryFulfilled, 'Не удалось возобновить поиск');
      },
    }),

    restartProject: builder.mutation<void, string>({
      query: (projectId) => ({
        url: `/api/communications/restart/${projectId}` satisfies ApiPath,
        method: 'GET',
      }),
      invalidatesTags: ['Products', 'Report', 'Projects'],
      onQueryStarted: (_arg, { queryFulfilled }) => {
        void notifyOnError(queryFulfilled, 'Не удалось перезапустить поиск');
      },
    }),

    restartProduct: builder.mutation<
      void,
      { reportId: string; productId: string; body?: RestartProductBody }
    >({
      query: ({ reportId, productId, body }) => ({
        url: `/api/reports/restart/${reportId}/products/${productId}` satisfies ApiPath,
        method: 'PATCH',
        ...(body && Object.keys(body).length ? { body } : {}),
      }),

      /* Здесь панели сбрасываем намеренно: позиция ищется заново, прежние
         аналоги и предложения устарели. */
      invalidatesTags: (_r, _e, { reportId, productId }) => [
        productsList(reportId),
        'Report',
        'Projects',
        { type: 'Products' as const, id: `marketplaces:${productId}` },
        { type: 'Products' as const, id: `portal:${productId}` },
        { type: 'Products' as const, id: `analogs:${productId}` },
      ],
      onQueryStarted: (_arg, { queryFulfilled }) => {
        void notifyOnError(queryFulfilled, 'Не удалось перезапустить поиск позиции');
      },
    }),

    fixDimensions: builder.mutation<void, FixDimensionsBody>({
      query: (body) => ({
        url: '/api/admin/incorrect_dimensions' satisfies ApiPath,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Products', 'Report'],
      onQueryStarted: (_arg, { queryFulfilled }) => {
        void notifyOnError(queryFulfilled, 'Не удалось сохранить габариты');
      },
    }),

    fixAnalogDimensions: builder.mutation<void, FixAnalogDimensionsBody>({
      query: (body) => ({
        url: '/api/admin/incorrect_dimensions/analog' satisfies ApiPath,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, { product_id }) => [
        { type: 'Products' as const, id: `analogs:${product_id}` },
      ],
    }),

    stopSearch: builder.mutation<void, string>({
      query: (projectId) => ({
        url: `/api/communications/cancel/${projectId}` satisfies ApiPath,
        method: 'GET',
      }),
      invalidatesTags: ['Products', 'Report', 'Projects'],
      onQueryStarted: (_arg, { queryFulfilled }) => {
        void notifyOnError(queryFulfilled, 'Не удалось остановить поиск');
      },
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetProjectsQuery,
  useCreateProjectMutation,
  useUpdateProjectMutation,
  useDeleteProjectMutation,
  useGetProjectReportQuery,
  useGetProductsQuery,
  useAddProductMutation,
  useUpdateProductMutation,
  useDeleteProductMutation,
  useUploadPositionsFileMutation,
  useSetDeliveryAddressMutation,
  useStartSearchMutation,
  useRestartProjectMutation,
  useRestartProductMutation,
  useFixDimensionsMutation,
  useFixAnalogDimensionsMutation,
  useStopSearchMutation,
  useStartProductsSearchMutation,
  useGetProductMarketplacesQuery,
  useGetSupplierPortalQuery,
  useGetReportDeliveryQuery,
  useGetProductAnalogsQuery,
  useMakeScreenshotsMutation,
  useCheckScreenshotsStatusQuery,
  useStartAnalogsLogisticsMutation,
  useAddProductSourceMutation,
  useDeleteSourcesMutation,
  useDeleteMarketplaceMutation,
  useDeleteSupplierPortalMutation,
  useGetProjectNoteQuery,
  useSaveProjectNoteMutation,
  useDeleteProjectNoteMutation,
} = projectApi;